import { useCallback } from "react";
import { cloneDeep } from "lodash";
import { dataModelToQueryString } from "@/entities/data-model";
import type { SearchDataModel } from "@/entities/data-model";
import { useMetadataParamsContext } from "@/entities/form-metadata";
import type { UserPreferences } from "@/entities/user-preferences";
import { useSubmitHandler } from "@/shared/submit-handler";
import type { FormValues } from "../form";
import { DEFAULT_GIVEN_NAME_EXACTNESS, DEFAULT_SURNAME_EXACTNESS } from "../form/utils";
import { mapFormEventsToDataModel } from "../metadata-converters/mapFormEventsToDataModel";

interface HandleSubmitProps {
    formValues: FormValues;
    userPreference: UserPreferences | null;
}

export const useFormControls = (dataModel: SearchDataModel, isEditForm: boolean) => {
    const metadataParams = useMetadataParamsContext();
    const customSubmitHandler = useSubmitHandler();
    const convertFormValuesToDataModel = useCallback(
        (formValues: FormValues) => {
            // Exclude passThroughData
            const { passThroughData, ...rest } = dataModel;
            const mappedFormEventsToDataModelEvents = mapFormEventsToDataModel(formValues);
            return {
                ...rest,
                filters: {
                    ...dataModel.filters,
                    recordSubject: formValues.recordSubject,
                },
                name: formValues.name,
                family: formValues.family,
                events: mappedFormEventsToDataModelEvents,
                treePerson: formValues.treePerson,
                types: formValues.types,
                priority: formValues.priority,
                location: formValues.location,
                fields: formValues.fields,
                showUnviewedRecordsOnly: formValues.showUnviewedRecordsOnly,
            } as SearchDataModel;
        },
        [dataModel],
    );

    const applyUserPreferences = useCallback(
        (newDataMode: SearchDataModel, userPreference: UserPreferences | null) => {
            const pageSize = isEditForm ? newDataMode.page.size : userPreference?.UserPreferences?.ResultsPageSize;
            return {
                ...newDataMode,
                page: {
                    ...newDataMode.page,
                    size: pageSize ?? 20,
                },
            };
        },
        [isEditForm],
    );

    const handleExactnessPresence = (formValues: FormValues) => {
        const formValuesCopy = cloneDeep(formValues);
        if (!formValuesCopy.name.givenName) {
            formValuesCopy.name.givenNameExactness = DEFAULT_GIVEN_NAME_EXACTNESS;
        }
        if (!formValuesCopy.name.surname) {
            formValuesCopy.name.surnameExactness = DEFAULT_SURNAME_EXACTNESS;
        }
        Object.values(formValuesCopy.family).forEach((relation) => {
            relation.instances.forEach((instance) => {
                if (!instance.givenName) {
                    instance.givenNameExactness = DEFAULT_GIVEN_NAME_EXACTNESS;
                }
                if (!instance.surname) {
                    instance.surnameExactness = DEFAULT_SURNAME_EXACTNESS;
                }
            });
        });
        Object.values(formValuesCopy.events).forEach((event) => {
            event.instances.forEach((instance) => {
                if (instance.dateProximity?.year != null) {
                    instance.dateProximity.month = 0;
                    instance.dateProximity.day = 0;
                }
                if (!instance.location) {
                    instance.locationExactness = null;
                }
            });
        });
        Object.values(formValuesCopy.fields).forEach((field) => {
            if (!field.value) {
                field.isExact = false;
            }
        });
        return formValuesCopy;
    };

    const handleSubmit = useCallback(
        async ({ formValues, userPreference }: HandleSubmitProps) => {
            const processedFormValues = handleExactnessPresence(formValues);

            let newDataModel = convertFormValuesToDataModel(processedFormValues);
            newDataModel = applyUserPreferences(newDataModel, userPreference);
            let query = dataModelToQueryString(newDataModel, "search");
            if (dataModel.passThroughData) {
                const delimiter = query.length ? "&" : "";
                query = `${query}${delimiter}${dataModel.passThroughData}`;
            }

            if (customSubmitHandler) {
                await customSubmitHandler({ dataModel: newDataModel, query });
            } else {
                let basePath = "/search";
                if ("contentType" in metadataParams && !!metadataParams.contentType) {
                    basePath =
                        metadataParams.contentType === "collection"
                            ? `/search/collections/${metadataParams.formId}`
                            : `/search/categories/${metadataParams.formId}`;
                }
                window.open(`${basePath}?${query}`, "_self");
                // Keep isSubmitting true until navigation completes
                return new Promise<void>(() => {});
            }
        },
        [dataModel, metadataParams, applyUserPreferences, convertFormValuesToDataModel, customSubmitHandler],
    );

    return {
        handleSubmit,
    };
};
